import { translateFile } from '@/i18n/utils';
import { type TranslationFileKey, type TranslationNamespace } from '@/i18n/translations';

/**
 * Structural stand-in for Joi's `ErrorReport`, so the translation layer does
 * not depend on Joi.
 */
type ValidationErrorReport = { code: string; message?: string };

/**
 * Translates validation messages when the rule fails, not when the schema is
 * built.
 *
 * Joi resolves `.messages()` at build time, so a module-level schema freezes
 * its copy on import — before the account preference has even loaded — and
 * never follows a language change. Passing this to `.error()` moves the lookup
 * to validation time, which keeps the schema an ordinary constant: no locale
 * parameter, no rebuild, no `useMemo` keyed on the language.
 *
 * Codes with no entry keep whatever message Joi produced.
 */
export function translatedMessages<Namespace extends TranslationNamespace>(
  namespace: Namespace,
  messagesByCode: Partial<Record<string, TranslationFileKey<Namespace>>>,
) {
  return <Report extends ValidationErrorReport>(errors: Report[]): Report[] =>
    errors.map((error) => {
      const key = messagesByCode[error.code];
      if (key) error.message = translateFile(namespace, key);
      return error;
    });
}
